import React, { useState, useRef } from "react";
import { GlobalImageRepository } from "../../shapes/GlobalImageRepository";
import "./SymbolLibraryManager.css";
import ConfirmationDialog from "./ConfirmationDialog";

export type SymbolLibraryManagerMode = "edit" | "pick";

interface SymbolLibraryManagerProps {
  initialImages: Record<string, string>;
  mode: SymbolLibraryManagerMode;
  onClose: () => void;
  onPick?: (id: string, image: HTMLImageElement) => void;
}

export const SymbolLibraryManager: React.FC<SymbolLibraryManagerProps> = ({
  initialImages,
  mode,
  onClose,
  onPick,
}) => {
  const [images, setImages] = useState<Record<string, string>>(initialImages);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState<null | { action: string; data?: any }>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddImages = async (files: FileList | null) => {
    if (!files) return;
    const newImages: Record<string, string> = {};
    for (const file of Array.from(files)) {
      const base64 = await fileToBase64(file);
      const id = crypto.randomUUID();
      newImages[id] = base64;
    }
    setImages((prev) => {
      const updated = { ...prev, ...newImages };
      if (mode === "pick" && Object.keys(newImages).length > 0) {
        const firstNewId = Object.keys(newImages)[0];
        setSelectedId(firstNewId);
        setPreviewId(firstNewId);
      }
      return updated;
    });
  };

  const fileToBase64 = (file: File) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleDelete = (id: string) => {
    setShowConfirm({ action: "delete", data: id });
  };

  const confirmDelete = (id: string) => {
    setImages((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    if (selectedId === id) {
      setSelectedId(null);
      setPreviewId(null);
    }
    setShowConfirm(null);
  };

  const handleCancel = () => {
    if (JSON.stringify(images) !== JSON.stringify(initialImages)) {
      setShowConfirm({ action: "cancel" });
    } else {
      onClose();
    }
  };

  const confirmCancel = (keepChanges: boolean) => {
    if (keepChanges) {
      saveToGlobalRepo();
    }
    setShowConfirm(null);
    onClose();
  };

  const saveToGlobalRepo = async () => {
    GlobalImageRepository.clear();
    for (const [id, base64] of Object.entries(images)) {
      await GlobalImageRepository.addImage(id, base64);
    }
  };

  const handleOk = async () => {
    await saveToGlobalRepo();
    if (mode === "pick" && selectedId && onPick) {
      const img = GlobalImageRepository.getImage(selectedId);
      if (img) {
        onPick(selectedId, img);
      }
    }
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>{mode === "edit" ? "Edit Images" : "Pick Image"}</h2>
        <div className="image-grid-container">
          <div className={`image-grid ${previewId ? "half-width" : ""}`}>
            {Object.entries(images).map(([id, base64]) => (
              <div
                key={id}
                className={`image-item ${selectedId === id ? "selected" : ""}`}
                onClick={() => {
                  setSelectedId(id);
                  setPreviewId(id);
                }}
              >
                <img src={base64} alt="" />
                <button
                  className="delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(id);
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          {previewId && (
            <div className="image-preview">
              <button className="close-preview" onClick={() => setPreviewId(null)}>×</button>
              <img src={images[previewId]} alt="Preview" />
            </div>
          )}
        </div>
        <div className="modal-controls">
          <button onClick={() => fileInputRef.current?.click()}>Add</button>
          <button onClick={handleCancel}>Cancel</button>
          <button
            onClick={handleOk}
            disabled={mode === "pick" && !selectedId}
          >
            {mode === "edit" ? "Save" : "Ok"}
          </button>
        </div>
        <input
          type="file"
          multiple
          accept="image/*"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={(e) => handleAddImages(e.target.files)}
        />
      </div>
      {showConfirm?.action === "delete" && (
        <ConfirmationDialog
          title="Delete Image"
          message="Are you sure you want to delete this image?"
          onConfirm={() => confirmDelete(showConfirm.data)}
          onCancel={() => setShowConfirm(null)}
        />
      )}
      {showConfirm?.action === "cancel" && (
        <ConfirmationDialog
          title="Unsaved Changes"
          message="Do you want to keep your changes?"
          onConfirm={() => confirmCancel(true)}
          onCancel={() => confirmCancel(false)}
        />
      )}
    </div>
  );
};